import { Typography, Form, Button, message, Space, Tag } from "antd";
import MasterComponent from "../../../components/Forms";
import { MasterConfigs } from "../../../interfaces/entity";
import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
const { Title } = Typography;

const TestSetup = () => {
    const [isAIInterview, setIsAIInterview] = useState(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const aiInterviewValue = Form.useWatch('isAIInterview', form);

    useEffect(() => {
        if (aiInterviewValue !== undefined) {
            setIsAIInterview(aiInterviewValue);
        }
    }, [aiInterviewValue]);

    const config: MasterConfigs = useMemo(() => ({
        api: () => '/online-test-settings',
        title: 'Test Setup',
        isActionTab: true,
        modalTitle: () => config.title,
        onDataLoad: (data: any) => {
            if (data && data.isAIInterview !== undefined) {
                setIsAIInterview(data.isAIInterview);
            }
        },
        handleValuesChange: (changedValues: any, allValues: any) => {
            if ('isAIInterview' in changedValues) {
                setIsAIInterview(changedValues.isAIInterview);
            }
        },
        getColumns: () => [
            {
                title: 'Test Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'MCQ Section',
                dataIndex: 'mcqQuestions',
                key: 'mcqQuestions',
                ellipsis: true,
                render: (mcqQuestions, record) => {
                    if (record.isAIInterview) {
                        return <span style={{ color: '#999' }}>-</span>;
                    }
                    return mcqQuestions?.map((item, index) => {
                        return <div key={index}>{index + 1 + '). '} {item.section?.name} ({item.questionCount}) ({item?.timeRequired}min)</div>
                    })
                }
            },
            {
                title: 'AI Interview Section',
                dataIndex: 'aiInterviewConfig',
                key: 'aiInterviewConfig',
                ellipsis: true,
                render: (aiInterviewConfig, record) => {
                    if (!record.isAIInterview) {
                        return <span style={{ color: '#999' }}>-</span>;
                    }
                    return aiInterviewConfig?.map((item, index) => {
                        return <div key={index}>{index + 1 + '). '} {item.section?.name} ({item.questionCount}) ({item?.timeRequired}min)</div>
                    })
                }
            },
            {
                title: 'Codding Section',
                dataIndex: 'coddingQuestions',
                key: 'coddingQuestions',
                ellipsis: true,
                render: (coddingQuestions) => {
                    return coddingQuestions?.map((item, index) => {
                        return <div key={index}>{index + 1 + '). '} {item.section?.name} ({item.questionCount}) ({item?.timeRequired}min)</div>
                    })
                }
            },
            {
                title: 'Total Question',
                dataIndex: 'mcqQuestions',
                key: 'totalQuestions',
                align: 'center',
                render: (mcqQuestions, records) => {
                    if (records.isAIInterview) {
                        // For AI Interview, show count from configuration
                        const aiConfig = records.aiInterviewConfig || [];
                        const aiCount = Array.isArray(aiConfig) ? aiConfig.reduce((acc, item) => acc + (item.questionCount || 0), 0) : 0;
                        const codingCount = records.coddingQuestions ? records.coddingQuestions.reduce((acc, item) => acc + (item.questionCount || 0), 0) : 0;
                        return <span style={{ fontWeight: 'bold' }}>{aiCount + codingCount}</span>;
                    }
                    // For MCQ/Coding tests, show total of MCQ + Coding questions
                    const mcqCount = mcqQuestions ? mcqQuestions.reduce((acc, item) => acc + (item.questionCount || 0), 0) : 0;
                    const codingCount = records.coddingQuestions ? records.coddingQuestions.reduce((acc, item) => acc + (item.questionCount || 0), 0) : 0;
                    return <span style={{ fontWeight: 'bold' }}>{mcqCount + codingCount}</span>;
                }
            },
            {
                title: 'Total Time Required',
                dataIndex: 'mcqQuestions',
                key: 'totalTime',
                align: 'center',
                render: (mcqQuestions, records) => {
                    if (records.isAIInterview) {
                        // AI Interview: sum of AI interview time + coding time if any
                        const aiConfig = records.aiInterviewConfig || [];
                        const aiTime = Array.isArray(aiConfig) ? aiConfig.reduce((acc, item) => acc + (item.timeRequired || 0), 0) : 0;
                        const codingTime = records.coddingQuestions ? records.coddingQuestions.reduce((acc, item) => acc + (item.timeRequired || 0), 0) : 0;
                        const totalTime = aiTime + codingTime;
                        return <span style={{ fontWeight: 'bold' }}>{totalTime} Minutes</span>;
                    }
                    const mcqTime = records.mcqQuestions ? records.mcqQuestions.reduce((acc, item) => acc + (item.timeRequired || 0), 0) : 0;
                    const codingTime = records.coddingQuestions ? records.coddingQuestions.reduce((acc, item) => acc + (item.timeRequired || 0), 0) : 0;
                    return <span style={{ fontWeight: 'bold' }}>{mcqTime + codingTime} Minutes</span>;
                }
            },
            {
                title: 'Test Type',
                dataIndex: 'isAIInterview',
                key: 'isAIInterview',
                align: 'center',
                render: (isAIInterview) => {
                    return isAIInterview ? <span style={{ color: '#1890ff', fontWeight: 'bold' }}>AI Interview</span> : <span>MCQ/Coding</span>
                }
            }
        ],
        getFormColumns: (): any[] => {
            const baseColumns: any[] = [
                {
                    label: 'Test Name',
                    name: 'name',
                    type: 'text',
                    rules: [{ required: true, message: 'Please enter name' }]
                },
                {
                    label: 'Enable AI Interview',
                    name: 'isAIInterview',
                    type: 'checkbox',
                    span: 24,
                    checkboxProps: {
                        onChange: (e: any) => {
                            setIsAIInterview(e.target.checked);
                        }
                    },
                    initialValue: false
                }
            ];

            if (isAIInterview) {
                return [
                    ...baseColumns,
                    {
                        type: 'custom',
                        formItem: () => {
                            return <Title level={5}>AI Interview Section (30 Minutes)</Title>
                        },
                        span: 24
                    },
                    {
                        type: 'form',
                        name: 'aiInterviewConfig',
                        span: 24,
                        form: [
                            {
                                label: 'Section Name',
                                name: 'section',
                                type: 'select',
                                isCombo: true,
                                span: 8,
                                rules: [{ required: true, message: 'Please select section' }]
                            },
                            {
                                label: 'Question Count',
                                name: 'questionCount',
                                type: 'number',
                                span: 6,
                                inputNumberProps: {
                                    min: 1,
                                    max: 120,
                                },
                                rules: [{ required: true, message: 'Please enter count' }]
                            },
                            {
                                label: 'Time Required',
                                name: 'timeRequired',
                                type: 'number',
                                span: 6,
                                inputNumberProps: {
                                    min: 1,
                                    max: 120,
                                    suffix: 'Minutes'
                                },
                                rules: [{ required: true, message: 'Please enter time' }]
                            }
                        ]
                    },
                    {
                        type: 'custom',
                        formItem: () => {
                            return <Title level={5}>Coding Section</Title>
                        },
                        span: 24
                    },
                    {
                        type: 'form',
                        name: 'coddingQuestions',
                        span: 24,
                        form: [
                            {
                                label: 'Section Name',
                                name: 'section',
                                type: 'select',
                                isCombo: true,
                                span: 8,
                                rules: [{ required: true, message: 'Please select section' }]
                            },
                            {
                                label: 'Question Count',
                                name: 'questionCount',
                                type: 'number',
                                span: 6,
                                inputNumberProps: {
                                    min: 1,
                                    max: 120,
                                },
                                rules: [{ required: true, message: 'Please enter count' }]
                            },
                            {
                                label: 'Time Required',
                                name: 'timeRequired',
                                type: 'number',
                                span: 6,
                                inputNumberProps: {
                                    min: 1,
                                    max: 120,
                                    suffix: 'Minutes'
                                },
                                rules: [{ required: true, message: 'Please enter time' }]
                            }
                        ]
                    }
                ];
            }

            return [
                ...baseColumns,
                {
                    type: 'custom',
                    formItem: () => {
                        return <Title level={5}>MCQ Section</Title>
                    },
                    span: 24
                },
                {
                    type: 'form',
                    name: 'mcqQuestions',
                    span: 24,
                    form: [
                        {
                            label: 'Section Name',
                            name: 'section',
                            type: 'select',
                            isCombo: true,
                            span: 8,
                            rules: [{ required: true, message: 'Please select section' }]
                        },
                        {
                            label: 'Question Count',
                            name: 'questionCount',
                            type: 'number',
                            span: 6,
                            inputNumberProps: {
                                min: 1,
                                max: 120,
                            },
                            rules: [{ required: true, message: 'Please enter count' }]
                        },
                        {
                            label: 'Time Required',
                            name: 'timeRequired',
                            type: 'number',
                            span: 6,
                            inputNumberProps: {
                                min: 1,
                                max: 120,
                                suffix: 'Minutes'
                            },
                            rules: [{ required: true, message: 'Please enter time' }]
                        }
                    ]
                },
                {
                    type: 'custom',
                    formItem: () => {
                        return <Title level={5}>Codding Section</Title>
                    },
                    span: 24
                },
                {
                    type: 'form',
                    name: 'coddingQuestions',
                    span: 24,
                    form: [
                        {
                            label: 'Section Name',
                            name: 'section',
                            type: 'select',
                            span: 8,
                            isCombo: true,
                            rules: [{ required: true, message: 'Please select section' }]
                        },
                        {
                            label: 'Question Count',
                            name: 'questionCount',
                            type: 'number',
                            span: 6,
                            inputNumberProps: {
                                min: 1,
                                max: 120,
                            },
                            rules: [{ required: true, message: 'Please enter count' }]
                        },
                        {
                            label: 'Time Required',
                            name: 'timeRequired',
                            type: 'number',
                            span: 6,
                            rules: [{ required: true, message: 'Please enter time' }],
                            inputNumberProps: {
                                min: 1,
                                max: 120,
                                suffix: 'Minutes'
                            }
                        }
                    ]
                }
            ];
        },
        combo: [
            {
                api: '/skills',
                name: 'section',
                idKey: '_id',
                render: (e) => e.name
            },
            {
                api: '/skills',
                name: 'skills',
                idKey: '_id',
                render: (e) => e.name
            }
        ],
        actions: [{ actionType: 'Add', name: 'Add' }]
    }), [isAIInterview]);

    return (
        <MasterComponent config={config} form={(formInstance) => {
            if (formInstance) {
                const aiInterviewVal = formInstance.getFieldValue('isAIInterview');
                if (aiInterviewVal !== undefined) {
                    setIsAIInterview(aiInterviewVal);
                }
            }
        }} />
    )
}

export default TestSetup;